import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Share2, RotateCcw, Sparkles, AlertCircle, MessageCircle, ShoppingBag, Check, FlaskConical, Copy, Users, Instagram, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ScoreRing from "@/components/ScoreRing";
import SocialProof from "@/components/SocialProof";
import Testimonials from "@/components/Testimonials";
import { GLOW } from "@/constants/testIds";
import { track, newEventId, trackCustom } from "@/lib/metaPixel";

const SEV = {
  alto: { c: "#C87979", label: "ALTO" },
  medio: { c: "#D4AF37", label: "MEDIO" },
  basso: { c: "#8BA888", label: "BASSO" },
};
const sev = (s) => SEV[(s || "medio").toLowerCase()] || SEV.medio;

const gradeColor = (g) => (g >= 6 ? "#C87979" : g >= 4 ? "#D4AF37" : "#8BA888");

const trackClick = (kind, reportId, nAnalisi, productHandle = null) => {
  try {
    api.post("/track/click", { report_id: reportId, n_analisi: nAnalisi, kind, product_handle: productHandle });
    window.dispatchEvent(new CustomEvent("glowy:cart-click", { detail: { kind } }));
    track("InitiateCheckout", { content_category: kind, content_type: "product" });
  } catch (e) { /* fire-and-forget */ }
};

const LOADING_STEPS = [
  "Stiamo esaminando la tua pelle…",
  "Analisi di texture e idratazione…",
  "Rilevamento di rughe e segni del tempo…",
  "Misurazione di luminosità, pori e tono…",
  "Confronto tra età cutanea ed età reale…",
  "Individuazione degli inestetismi principali…",
  "Elaborazione della tua routine su misura…",
];

const ScanProgress = () => {
  const [pct, setPct] = useState(4);
  useEffect(() => {
    const t = setInterval(() => {
      setPct((p) => (p >= 95 ? 95 : p + Math.max(0.4, (96 - p) * 0.045)));
    }, 550);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mt-6 w-72 max-w-[82vw]" data-testid="scan-progress">
      <div className="flex items-end justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Elaborazione</span>
        <span className="font-display text-3xl text-[#D8B28C] tabular-nums" data-testid="scan-progress-pct">{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#D8B28C,#C17F59)" }}
          animate={{ width: `${pct}%` }} transition={{ ease: "easeOut", duration: 0.5 }} />
      </div>
    </div>
  );
};

const LoadingSteps = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % LOADING_STEPS.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mt-6 h-20 sm:h-16 overflow-hidden flex items-center justify-center" data-testid="loading-step">
      <motion.p key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="font-display text-2xl sm:text-3xl lg:text-4xl text-white text-center leading-snug px-4">
        {LOADING_STEPS[i]}
      </motion.p>
    </div>
  );
};

const AGE_LVL = {
  ottimo: "#8BA888",
  buono: "#D4AF37",
  migliorabile: "#C17F59",
};

const AgeCard = ({ a }) => {
  const c = AGE_LVL[a.livello] || AGE_LVL.buono;
  return (
    <div data-testid="age-assessment" className="mt-5 rounded-3xl bg-white p-6 sm:p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)", borderLeft: `4px solid ${c}` }}>
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#7A7571] mb-2">Età della pelle</p>
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium text-white" style={{ background: c }}>
          <Sparkles className="h-4 w-4" /> {a.giudizio}
        </span>
        <div className="flex items-center gap-4 text-sm text-[#7A7571]">
          {a.eta_anagrafica != null && <span>Anagrafica: <strong className="text-[#2D2825]">{a.eta_anagrafica}</strong></span>}
          {a.eta_cutanea_stimata != null && <span>Cutanea stimata: <strong className="text-[#2D2825]">{a.eta_cutanea_stimata}</strong></span>}
        </div>
      </div>
      {a.messaggio && <p className="mt-3 text-[#2D2825]">{a.messaggio}</p>}
    </div>
  );
};


export default function Result() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [contact, setContact] = useState({ cognome: "", cellulare: "", email: "" });
  const [savingContact, setSavingContact] = useState(false);
  const contactComplete = contactDone || !!(data?.lead?.cellulare && data?.lead?.email);

  useEffect(() => {
    let timer, active = true, attempts = 0;
    const MAX = 60;
    const poll = async () => {
      try {
        const res = await api.get(`/report/${id}`);
        if (!active) return;
        setData(res.data);
        if (res.data.status === "processing") {
          attempts += 1;
          if (attempts >= MAX) { setData({ ...res.data, status: "error", error: "L'analisi sta impiegando troppo tempo. Riprova." }); return; }
          timer = setTimeout(poll, 2500);
        } else if (res.data.status === "done" && res.data.offer_status === "building") {
          // report già visibile: continuiamo solo a caricare i carrelli in background
          attempts += 1;
          if (attempts >= MAX) return;
          timer = setTimeout(poll, 1200);
        }
      } catch (e) { if (active) setError(true); }
    };
    poll();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [id]);

  useEffect(() => {
    if (contactComplete && data?.status === "done" && (data?.shopify_offer?.basic || data?.shopify_offer?.premium)) {
      window.__glowyReportReady = id;
      const fire = () => window.dispatchEvent(new CustomEvent("glowy:report-ready", { detail: { reportId: id } }));
      fire();
      const t = setTimeout(fire, 1500);
      return () => clearTimeout(t);
    }
  }, [data?.status, id, contactComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const analysisTracked = useRef(false);
  useEffect(() => {
    if (data?.status === "done" && !analysisTracked.current) {
      analysisTracked.current = true;
      trackCustom("AnalisiCompletata", { glow_score: data?.report?.glow_score, report_id: id });
    }
  }, [data?.status, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const SHARE_TEXT = "✨ Ho scoperto il mio Glow Score con Skin Lovers — un'analisi del viso AI gratuita! Prova anche tu e scopri il punteggio della tua pelle 🌿 Aiuta un'amica ad analizzare il suo viso 👉";

  const share = async () => {
    const url = window.location.origin + "/";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Il mio Glow Score ✨ · Skin Lovers", text: SHARE_TEXT, url });
      } else {
        await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
        toast.success("Messaggio copiato! Incollalo in una storia o post 💛");
      }
    } catch (e) { /* utente ha annullato */ }
  };

  const shareWhatsApp = () => {
    const url = window.location.origin + "/";
    window.open(`https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${url}`)}`, "_blank");
  };

  const copyShare = async () => {
    const url = window.location.origin + "/";
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
      toast.success("Testo + link copiati! Incollali dove vuoi 💛");
    } catch (e) { toast.error("Copia non riuscita, riprova"); }
  };

  const contactInputCls = "w-full rounded-xl border border-[#E8E6E1] bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#C17F59]";

  const submitContact = async () => {
    if (!contact.cellulare.trim() || !contact.email.trim()) { toast.error("Inserisci cellulare ed email per ricevere il report"); return; }
    setSavingContact(true);
    try {
      await api.patch(`/report/${id}/contact`, contact);
      const leadEventId = newEventId("lead");
      track("Lead", {}, leadEventId);
      api.post("/events/lead", { email: contact.email, cellulare: contact.cellulare, report_id: id, event_id: leadEventId, event_source_url: window.location.href }).catch(() => {});
      setData((d) => (d ? { ...d, lead: { ...(d.lead || {}), ...contact } } : d));
      setContactDone(true);
      toast.success("Perfetto! Ecco il tuo Glow Score ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito, riprova");
      setSavingContact(false);
    }
  };

  if (error || data?.status === "error") {
    return (
      <div data-testid={GLOW.errorMessage} className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <AlertCircle className="h-10 w-10 text-[#C87979]" />
        <p className="font-display text-2xl text-[#2D2825]">{data?.error || "Report non trovato"}</p>
        <button onClick={() => navigate("/")} className="rounded-full bg-[#C17F59] px-6 py-3 text-white hover:bg-[#a96a47]">Riprova</button>
      </div>
    );
  }

  if (!data || data.status === "processing" || !contactComplete) {
    const frontalImg = data?.images?.frontal;
    const analysisDone = data?.status === "done";
    return (
      <div data-testid={GLOW.loadingState} className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
        style={{ background: "radial-gradient(circle at 50% 20%, #241c17, #14100d 78%)" }}>
        <div className="relative h-56 w-56 sm:h-64 sm:w-64 overflow-hidden rounded-[2rem] border border-[#C17F59]/40"
          style={{ boxShadow: "0 0 70px rgba(193,127,89,0.35), inset 0 0 40px rgba(0,0,0,0.45)" }}>
          {frontalImg ? (
            <img src={frontalImg} alt="scan" className="absolute inset-0 h-full w-full object-cover" style={{ filter: "saturate(0.92) contrast(1.05)" }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1a1512]">
              <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.8, repeat: Infinity }}>
                <Sparkles className="h-14 w-14 text-[#C17F59]" />
              </motion.div>
            </div>
          )}
          <div className="gs-scan-tint" />
          <div className="gs-scan-grid" />
          {!analysisDone && <div className="gs-face-scanline" />}
          <span className="gs-corner tl" /><span className="gs-corner tr" />
          <span className="gs-corner bl" /><span className="gs-corner br" />
          {!analysisDone && <>
            <span className="gs-scan-point" style={{ top: "34%", left: "35%" }} />
            <span className="gs-scan-point" style={{ top: "34%", left: "62%", animationDelay: "0.4s" }} />
            <span className="gs-scan-point" style={{ top: "56%", left: "50%", animationDelay: "0.8s" }} />
            <span className="gs-scan-point" style={{ top: "70%", left: "41%", animationDelay: "1.2s" }} />
          </>}
        </div>
        <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-[#C17F59]">
          <motion.span animate={{ opacity: [0.25, 1, 0.25] }} transition={{ duration: 1.4, repeat: Infinity }} className="inline-block h-2 w-2 rounded-full bg-[#C17F59]" />
          {analysisDone ? "Analisi pronta" : "Skin Mapping in corso"}
        </div>
        <div className="mt-4"><SocialProof variant="dark" showPosition /></div>
        <Testimonials variant="dark" className="mt-5 w-full max-w-md" />
        {analysisDone
          ? <p className="mt-4 font-display text-2xl sm:text-3xl text-white text-center leading-snug px-4">La tua analisi è pronta ✨</p>
          : (<><ScanProgress /><LoadingSteps /></>)}

        {!contactComplete && (
          <div data-testid="contact-gate" className="mt-8 w-full max-w-md rounded-3xl bg-[#FBF7F3] p-6 sm:p-7" style={{ boxShadow: "0 12px 50px rgba(0,0,0,0.45)" }}>
            <h3 className="font-display text-xl text-[#2D2825] text-center">Dove vuoi ricevere il tuo Glow Score? 📩</h3>
            <p className="mt-1.5 text-xs text-[#7A7571] text-center">Ti inviamo il report completo e i consigli personalizzati <strong>anche via email e WhatsApp</strong>. Completa per sbloccare il tuo risultato.</p>
            <div className="mt-4 space-y-3">
              <input data-testid="contact-cognome" className={contactInputCls} placeholder="Cognome"
                value={contact.cognome} onChange={(e) => setContact((c) => ({ ...c, cognome: e.target.value }))} />
              <input data-testid="contact-cellulare" className={contactInputCls} type="tel" placeholder="Cellulare (WhatsApp)"
                value={contact.cellulare} onChange={(e) => setContact((c) => ({ ...c, cellulare: e.target.value }))} />
              <input data-testid="contact-email" className={contactInputCls} type="email" placeholder="Email"
                value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} />
            </div>
            <button data-testid="contact-submit" disabled={savingContact} onClick={submitContact}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C17F59] px-6 py-3.5 text-white font-medium hover:bg-[#a96a47] active:scale-95 disabled:opacity-50 transition-all">
              {savingContact ? "Un attimo…" : (analysisDone ? "Vedi il mio Glow Score ✨" : "Completa e ricevi il report")}
            </button>
            <p className="mt-2 text-[10px] text-[#B9B4AE] text-center">I tuoi dati sono al sicuro e usati solo per l'analisi.</p>
          </div>
        )}
        {!contactComplete && !analysisDone && <p className="mt-4 text-xs text-white/40 text-center">Stiamo analizzando la tua pelle… nel frattempo lascia i tuoi dati 👆</p>}
      </div>
    );
  }

  const r = data.report || {};
  const lead = data.lead || {};
  const imgs = data.images || {};
  const prof = r.skin_profile || {};
  const indicators = r.clinical_indicators || [];
  const aging = r.aging_analysis || {};
  const zones = aging.zones || [];
  const inest = r.inestetismi || [];
  const palette = r.palette || {};
  const barr = r.barriera || {};
  const ann = r.annotations || {};
  const offer = data.shopify_offer;
  const dateStr = new Date(data.created_at).toLocaleDateString("it-IT");

  const buildStoryCanvas = async () => {
    try { await document.fonts.ready; } catch (e) { /* ok */ }
    const W = 1080, H = 1920;
    const cv = document.createElement("canvas");
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    // sfondo crema caldo
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#FBF7F3"); g.addColorStop(1, "#EAD8CA");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    // cornice sottile
    ctx.strokeStyle = "rgba(193,127,89,0.5)"; ctx.lineWidth = 3;
    ctx.strokeRect(48, 48, W - 96, H - 96);
    ctx.textAlign = "center";
    // wordmark
    ctx.fillStyle = "#2D2825"; ctx.font = "700 58px 'Playfair Display', Georgia, serif";
    ctx.fillText("SK+L  SKIN LOVERS", W / 2, 220);
    ctx.fillStyle = "#C17F59"; ctx.font = "600 30px 'Manrope', sans-serif";
    ctx.fillText("G L O W   S C O R E", W / 2, 280);
    // ring
    const cx = W / 2, cy = 760, R = 240;
    const score = Math.max(0, Math.min(100, Number(r.glow_score) || 0));
    ctx.lineCap = "round"; ctx.lineWidth = 40;
    ctx.strokeStyle = "#E6D4C6"; ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
    const start = -Math.PI / 2, end = start + (score / 100) * Math.PI * 2;
    const rg = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
    rg.addColorStop(0, "#D8B28C"); rg.addColorStop(1, "#C17F59");
    ctx.strokeStyle = rg; ctx.beginPath(); ctx.arc(cx, cy, R, start, end); ctx.stroke();
    // numero
    ctx.fillStyle = "#2D2825"; ctx.font = "700 230px 'Playfair Display', Georgia, serif";
    ctx.fillText(String(score), cx, cy + 78);
    ctx.fillStyle = "#7A7571"; ctx.font = "500 44px 'Manrope', sans-serif";
    ctx.fillText("/ 100", cx, cy + 168);
    // nome
    const nome = (lead.nome || "").trim();
    ctx.fillStyle = "#2D2825"; ctx.font = "italic 600 62px 'Playfair Display', Georgia, serif";
    ctx.fillText(nome ? `Il Glow Score di ${nome}` : "Il mio Glow Score", W / 2, 1210);
    // tipo pelle
    if (prof.tipologia) {
      ctx.fillStyle = "#C17F59"; ctx.font = "600 38px 'Manrope', sans-serif";
      ctx.fillText(`Pelle ${String(prof.tipologia).toUpperCase()}`, W / 2, 1290);
    }
    // CTA
    ctx.fillStyle = "#2D2825"; ctx.font = "700 46px 'Playfair Display', Georgia, serif";
    ctx.fillText("Fai anche tu il tuo Glow Score", W / 2, 1660);
    ctx.fillStyle = "#7A7571"; ctx.font = "500 40px 'Manrope', sans-serif";
    ctx.fillText("gratis · glowscore.skinloverscosmetics.com", W / 2, 1730);
    return cv;
  };

  const shareStoryImage = async () => {
    toast.info("Creo la tua immagine…");
    try {
      const cv = await buildStoryCanvas();
      cv.toBlob(async (blob) => {
        if (!blob) { toast.error("Immagine non creata, riprova"); return; }
        const file = new File([blob], "glow-score.png", { type: "image/png" });
        const url = window.location.origin + "/";
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: "Il mio Glow Score ✨", text: `${SHARE_TEXT} ${url}` });
            return;
          } catch (e) { /* annullato o non supportato → download */ }
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = "glow-score.png";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        toast.success("Immagine salvata! Caricala nella tua storia 💛");
      }, "image/png");
    } catch (e) { toast.error("Non riesco a creare l'immagine, riprova"); }
  };

  return (
    <div data-testid={GLOW.reportPage} className="relative z-10 mx-auto max-w-5xl px-4 sm:px-8 pt-10 pb-44 sm:pb-12">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b-2 border-[#2D2825] pb-4">
        <div>
          <p className="font-display text-2xl tracking-tight text-[#2D2825]">SK<span className="text-[#C17F59]">+</span>L <span className="text-sm tracking-[0.3em] align-middle">SKIN LOVERS</span></p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#7A7571]">Documento di consulenza · Skin Mapping</p>
        </div>
        <div className="flex gap-2">
          <button data-testid={GLOW.shareButton} onClick={share} className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-4 py-2 text-sm hover:bg-[#FAF9F6]"><Share2 className="h-4 w-4" /> Condividi</button>
          <button data-testid={GLOW.retryButton} onClick={() => navigate("/")} className="inline-flex items-center gap-2 rounded-full bg-[#C17F59] px-4 py-2 text-sm text-white hover:bg-[#a96a47]"><RotateCcw className="h-4 w-4" /> Nuova analisi</button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <Field label="N° Analisi" value={<span className="font-mono text-[#C17F59]">{data.n_analisi}</span>} />
        <Field label="Nome" value={lead.nome} />
        <Field label="Cognome" value={lead.cognome} />
        <Field label="Cellulare" value={lead.cellulare} />
        <Field label="Data analisi" value={dateStr} />
      </div>

      <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight text-[#2D2825]">Analisi della <em>pelle</em>.</h1>

      {/* Foto con annotazioni */}
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <PhotoPanel title="Frontale" src={imgs.frontal} points={ann.frontal || []} />
        <PhotoPanel title="Laterale" src={imgs.lateral} points={ann.lateral || []} />
      </div>

      {/* Score + summary */}
      <div className="mt-8 flex flex-col md:flex-row items-center gap-8 rounded-3xl bg-white p-8" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.08)" }}>
        <ScoreRing score={r.glow_score || 0} testId={GLOW.scoreRing} />
        <div className="flex-1 text-center md:text-left">
          <p className="font-display text-2xl text-[#2D2825] leading-snug">{r.summary}</p>
          {r.encouragement && <p className="mt-3 text-[#7A7571]">{r.encouragement}</p>}
        </div>
      </div>

      {/* Giudizio età cutanea */}
      {r.age_assessment && <AgeCard a={r.age_assessment} />}

      {/* Il tuo cruccio */}
      {r.concern_focus?.concern && (
        <div data-testid="concern-focus" className="mt-5 rounded-3xl bg-[#C17F59]/8 p-6 sm:p-7 border border-[#C17F59]/25">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#a96a47] mb-2">Il tuo obiettivo principale</p>
          <p className="font-display text-xl text-[#2D2825]">{r.concern_focus.concern}</p>
          {r.concern_focus.come_lo_miglioriamo && <p className="mt-2 text-[#5c5652]">{r.concern_focus.come_lo_miglioriamo}</p>}
        </div>
      )}

      {/* Aging analysis */}
      {zones.length > 0 && (
        <Section title="Analisi aging · 8 gradi per zona" note={aging.grado_medio ? `Grado medio · ${aging.grado_medio} / 8` : ""}>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {zones.map((z) => (
              <div key={z.zone} className="flex items-center gap-3">
                <span className="w-40 text-sm text-[#2D2825]">{z.zone}</span>
                <div className="flex gap-1 flex-1">
                  {[1,2,3,4,5,6,7,8].map((n) => (
                    <div key={n} className="flex-1 h-6 rounded flex items-center justify-center text-[10px] border"
                      style={n === Math.round(z.grade) ? { background: gradeColor(z.grade), color: "#fff", borderColor: gradeColor(z.grade) } : { borderColor: "#E8E6E1", color: "#B9B4AE" }}>{n}</div>
                  ))}
                </div>
                <span className="text-sm font-medium" style={{ color: gradeColor(z.grade) }}>{z.grade}/8</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Profilo + Indicatori */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
          <h3 className="font-display text-xl text-[#2D2825] mb-4">Profilo cutaneo</h3>
          <IdentRow k="Tipologia" v={prof.tipologia} />
          <IdentRow k="Sottotono" v={prof.sottotono} />
          <IdentRow k="Fototipo (Fitzpatrick)" v={prof.fototipo} />
          <IdentRow k="Stato barriera" v={prof.stato_barriera} />
          <IdentRow k="Sensibilità" v={prof.sensibilita} />
          <IdentRow k="Elasticità" v={prof.elasticita} last />
        </div>
        <div className="rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
          <h3 className="font-display text-xl text-[#2D2825] mb-4">Indicatori clinici</h3>
          <div className="space-y-3">
            {indicators.map((it) => (
              <div key={it.label}>
                <div className="flex justify-between text-sm mb-1"><span className="text-[#2D2825]">{it.label}</span><span className="text-[#7A7571]">{it.value}%</span></div>
                <div className="h-2 rounded-full bg-[#E8E6E1] overflow-hidden">
                  <motion.div className="h-full rounded-full bg-[#C17F59]" initial={{ width: 0 }} animate={{ width: `${Math.max(0, Math.min(100, it.value))}%` }} transition={{ duration: 0.9 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inestetismi */}
      {inest.length > 0 && (
        <Section title="Inestetismi rilevati">
          <div className="flex flex-wrap gap-3">
            {inest.map((it, i) => {
              const s = sev(it.severity);
              return (
                <span key={`${it.name}-${i}`} className="inline-flex items-center gap-2 rounded-lg border border-[#E8E6E1] bg-white px-3 py-2 text-sm">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.c }} />
                  {it.name}
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${s.c}22`, color: s.c }}>{s.label}</span>
                </span>
              );
            })}
          </div>
        </Section>
      )}

      {/* Palette + Barriera */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {(palette.swatches || []).length > 0 && (
          <div className="rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
            <h3 className="font-display text-xl text-[#2D2825] mb-4">Sottotono & palette</h3>
            <div className="flex rounded-xl overflow-hidden">
              {palette.swatches.map((c, i) => (
                <div key={`${c}-${i}`} className="flex-1 h-20 flex items-end justify-center pb-1" style={{ background: c }}>
                  <span className="text-[9px] font-mono bg-white/70 px-1 rounded">{c}</span>
                </div>
              ))}
            </div>
            {palette.base_consigliata && <p className="mt-3 text-sm text-[#7A7571]">Base consigliata · <strong className="text-[#2D2825]">{palette.base_consigliata}</strong></p>}
          </div>
        )}
        {(barr.indice != null || barr.descrizione) && (
          <div className="rounded-3xl bg-white p-7" style={{ boxShadow: "0 8px 32px rgba(45,40,37,0.06)" }}>
            <h3 className="font-display text-xl text-[#2D2825] mb-1">Stato della barriera</h3>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7571] mb-4">Film idrolipidico</p>
            <div className="flex gap-1.5 mb-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex-1 h-6 rounded" style={{ background: i < Math.round(barr.indice || 0) ? "#C17F59" : "#E8E6E1" }} />
              ))}
            </div>
            <p className="text-sm text-[#7A7571]"><span className="font-mono text-[#C17F59]">{barr.indice}/10</span> — {barr.descrizione}</p>
          </div>
        )}
      </div>

      {/* Carrelli Basic / Premium */}
      {offer && (offer.basic || offer.premium) ? (
        <Section title="Le tue routine consigliate" note="Codici personalizzati · validi 24 ore">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {offer.basic && <CartCard tier={offer.basic} kind="Basic" highlight={false} testId={GLOW.basicCart} onTrack={() => trackClick("base", id, data.n_analisi)} />}
            {offer.premium && <CartCard tier={offer.premium} kind="Premium" highlight testId={GLOW.premiumCart} onTrack={() => trackClick("premium", id, data.n_analisi)} />}
          </div>
          <Testimonials count={2} className="mt-6" />
        </Section>
      ) : data.offer_status === "building" ? (
        <Section title="Le tue routine consigliate" note="Ultimo step ✨">
          <OfferBuilding />
        </Section>
      ) : null}

      {offer?.single?.products?.length > 0 && <DownsellBlock single={offer.single} reportId={id} nAnalisi={data.n_analisi} />}

      {/* Condivisione invitante */}
      <div data-testid="share-card" className="mt-8 rounded-3xl p-7 sm:p-8 text-center"
        style={{ background: "linear-gradient(135deg,#C17F59,#a96a47)", boxShadow: "0 8px 40px rgba(193,127,89,0.3)" }}>
        <Users className="h-8 w-8 text-white/90 mx-auto" />
        <h2 className="mt-3 font-display text-2xl sm:text-3xl text-white leading-snug">Aiuta un'amica a scoprire la sua pelle ✨</h2>
        <p className="mt-2 text-sm text-white/90 max-w-md mx-auto">Condividi il Glow Score: le basta un minuto e due selfie per ricevere la sua analisi gratuita. Testo e link sono già pronti!</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button data-testid="share-story-image" onClick={shareStoryImage}
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[#a96a47] font-semibold hover:bg-white/90 transition-colors">
            <Instagram className="h-4 w-4" /> Immagine per la storia
          </button>
          <button data-testid="share-card-button" onClick={share}
            className="inline-flex items-center gap-2 rounded-full bg-white/15 px-6 py-3 text-white font-medium hover:bg-white/25 transition-colors">
            <Share2 className="h-4 w-4" /> Condividi link
          </button>
          <button data-testid="share-whatsapp" onClick={shareWhatsApp}
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-white font-medium hover:brightness-95 transition-all">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
          <button data-testid="share-copy" onClick={copyShare}
            className="inline-flex items-center gap-2 rounded-full bg-white/15 px-6 py-3 text-white font-medium hover:bg-white/25 transition-colors">
            <Copy className="h-4 w-4" /> Copia
          </button>
        </div>
      </div>

      {/* WhatsApp */}
      <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#8BA888]/10 p-5">
        <MessageCircle className="h-5 w-5 text-[#5f7d5c] mt-0.5 shrink-0" />
        <p className="text-sm text-[#5f7d5c]">Riceverai questo report e i consigli personalizzati <strong>anche su WhatsApp</strong> al numero {lead.cellulare}.</p>
      </div>

      <p className="mt-8 text-center text-xs text-[#7A7571]">Analisi generata dal motore AI Skin Lovers (metodo Valentina Coscione) · Non sostituisce un consulto dermatologico professionale.</p>
    </div>
  );
}

const BUILD_STEPS = [
  { at: 0, label: "Leggo i tuoi inestetismi e il profilo cutaneo" },
  { at: 20, label: "Confronto le formule Skin Lovers una per una" },
  { at: 42, label: "Verifico gli INCI e gli attivi compatibili con la tua pelle" },
  { at: 64, label: "Compongo la tua routine base e la routine completa" },
  { at: 84, label: "Genero il tuo codice sconto personale (valido 24h)" },
];

const ACTIVES = ["Acido Ialuronico", "Vitamina C", "Retinaldeide", "Niacinamide", "Papaina",
  "Bakuchiol", "Peptidi biomimetici", "Squalano", "Acido Salicilico", "Coenzima Q10",
  "Centella Asiatica", "Vitamina E", "Acido Mandelico", "Pantenolo"];

const EXPECTED_MS = 14000;

const OfferBuilding = () => {
  const [pct, setPct] = useState(3);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => {
      const k = Math.min(1, (Date.now() - start) / EXPECTED_MS);
      // curva easing: parte veloce, rallenta e si ferma a 97
      setPct(3 + 94 * (1 - Math.pow(1 - k, 2.2)));
    }, 120);
    const a = setInterval(() => setActive((i) => (i + 1) % ACTIVES.length), 900);
    return () => { clearInterval(t); clearInterval(a); };
  }, []);

  const eta = Math.max(1, Math.ceil((EXPECTED_MS * (1 - pct / 100)) / 1000));
  const currentStep = BUILD_STEPS.reduce((acc, s, i) => (pct >= s.at ? i : acc), 0);

  return (
    <div data-testid="offer-building" className="relative overflow-hidden rounded-3xl p-7 sm:p-10"
      style={{ background: "linear-gradient(150deg,#37543a,#5f7d5c 55%,#8BA888)", boxShadow: "0 10px 50px rgba(55,84,58,0.4)" }}>
      {/* scia luminosa ipnotica */}
      <motion.div aria-hidden className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3"
        style={{ background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)" }}
        animate={{ x: ["0%", "420%"] }} transition={{ duration: 2.6, repeat: Infinity, ease: "linear" }} />
      <motion.div aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full"
        style={{ background: "radial-gradient(circle,rgba(255,255,255,0.22),transparent 65%)" }}
        animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.85, 0.5] }} transition={{ duration: 3.4, repeat: Infinity }} />

      <div className="relative">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <motion.span className="absolute inset-0 rounded-full border border-white/40"
              animate={{ scale: [1, 1.6], opacity: [0.6, 0] }} transition={{ duration: 1.8, repeat: Infinity }} />
            <div className="h-12 w-12 rounded-full bg-white/15 grid place-items-center">
              <motion.div animate={{ rotate: [0, 14, -14, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                <FlaskConical className="h-6 w-6 text-white" />
              </motion.div>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/70">Laboratorio Skin Lovers</p>
            <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight">Sto costruendo le tue routine su misura</h3>
          </div>
        </div>

        {/* attivo in scansione */}
        <div className="mt-6 flex items-center gap-3 rounded-2xl bg-black/15 px-4 py-3">
          <Sparkles className="h-4 w-4 text-white/80 shrink-0" />
          <span className="text-xs uppercase tracking-[0.2em] text-white/60 shrink-0">Analizzo</span>
          <motion.span key={active} data-testid="offer-active-ingredient"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="font-display text-lg text-white truncate">{ACTIVES[active]}</motion.span>
        </div>

        {/* step */}
        <ul className="mt-6 space-y-2.5" data-testid="offer-steps">
          {BUILD_STEPS.map((s, i) => {
            const done = i < currentStep;
            const now = i === currentStep;
            return (
              <li key={s.label} className="flex items-center gap-3">
                <span className={`h-5 w-5 shrink-0 rounded-full grid place-items-center transition-colors ${done ? "bg-white" : now ? "bg-white/25" : "bg-white/10"}`}>
                  {done ? <Check className="h-3 w-3 text-[#5f7d5c]" strokeWidth={3} />
                    : now ? <motion.span className="h-1.5 w-1.5 rounded-full bg-white" animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1, repeat: Infinity }} />
                      : <span className="h-1.5 w-1.5 rounded-full bg-white/40" />}
                </span>
                <span className={`text-sm ${done ? "text-white/70" : now ? "text-white font-medium" : "text-white/45"}`}>{s.label}</span>
              </li>
            );
          })}
        </ul>

        {/* barra + countdown */}
        <div className="mt-7">
          <div className="flex items-end justify-between mb-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/75">Composizione routine</span>
            <span className="font-display text-3xl text-white tabular-nums" data-testid="offer-progress-pct">{Math.round(pct)}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-white/20 overflow-hidden">
            <motion.div className="h-full rounded-full bg-white" animate={{ width: `${pct}%` }} transition={{ ease: "linear", duration: 0.2 }} />
          </div>
          <p className="mt-2 text-xs text-white/75 tabular-nums" data-testid="offer-eta">{pct >= 96 ? "Ultimi ritocchi, ci siamo…" : `Pronte tra circa ${eta} second${eta === 1 ? "o" : "i"}`}</p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <SocialProof variant="dark" />
        </div>
        <Testimonials variant="dark" className="mt-4" />

        <div className="mt-4 flex items-start gap-3 rounded-2xl bg-white/12 px-4 py-3" data-testid="offer-stay-warning">
          <AlertCircle className="h-4 w-4 text-white shrink-0 mt-0.5" />
          <p className="text-sm text-white/90">Resta su questa pagina: il tuo <strong>codice sconto è personale</strong> e viene creato solo adesso, con scadenza 24 ore. Se esci ora lo perdi.</p>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7571]">{label}</p><p className="text-[#2D2825] font-medium">{value || "—"}</p></div>
);

const Section = ({ title, note, children }) => (
  <div className="mt-8">
    <div className="flex items-baseline justify-between mb-4 border-b border-[#E8E6E1] pb-2">
      <h2 className="font-display text-2xl text-[#2D2825]">{title}</h2>
      {note && <span className="text-xs uppercase tracking-[0.15em] text-[#C17F59]">{note}</span>}
    </div>
    {children}
  </div>
);

const IdentRow = ({ k, v, last }) => (
  <div className={`flex justify-between py-2.5 ${last ? "" : "border-b border-[#E8E6E1]"}`}>
    <span className="text-xs uppercase tracking-[0.15em] text-[#7A7571]">{k}</span>
    <span className="font-display text-[#2D2825]">{v || "—"}</span>
  </div>
);

const PhotoPanel = ({ title, src, points }) => (
  <div className="rounded-2xl overflow-hidden border border-[#E8E6E1] bg-white">
    <div className="flex justify-between bg-[#2D2825] text-white px-4 py-2 text-xs uppercase tracking-[0.2em]"><span>{title}</span></div>
    <div className="relative">
      {src && <img src={src} alt={title} className="w-full h-[360px] object-cover" />}
      {points.map((p) => {
        const s = sev(p.severity);
        return (
          <div key={p.n} className="absolute -translate-x-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-white shadow"
            style={{ left: `${(p.x ?? 0.5) * 100}%`, top: `${(p.y ?? 0.5) * 100}%`, background: s.c }}>{p.n}</div>
        );
      })}
    </div>
    <div className="p-4 space-y-1.5">
      {points.map((p) => {
        const s = sev(p.severity);
        return (
          <div key={p.n} className="flex items-center gap-2 text-sm">
            <span className="h-5 w-5 rounded-full text-white text-[10px] flex items-center justify-center shrink-0" style={{ background: s.c }}>{p.n}</span>
            <span className="text-[#2D2825] flex-1">{p.label}</span>
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${s.c}22`, color: s.c }}>{s.label}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const DownsellBlock = ({ single, reportId, nAnalisi }) => {
  const products = single.products || [];
  const [sel, setSel] = useState(products[0]?.handle);
  const chosen = products.find((p) => p.handle === sel) || products[0];
  return (
    <div data-testid="downsell-block" className="mt-8 rounded-3xl border-2 border-dashed border-[#8BA888]/60 bg-white p-7">
      <h2 className="font-display text-2xl text-[#2D2825]">Non sei pronta per la routine completa?</h2>
      <p className="mt-1 text-[#7A7571] text-sm">Inizia con <strong className="text-[#2D2825]">1 solo prodotto a tua scelta</strong> al <strong className="text-[#5f7d5c]">{single.discount_pct}%</strong> — provalo senza pensieri.</p>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {products.map((p) => {
          const active = p.handle === sel;
          return (
            <button key={p.handle} data-testid={`downsell-pick-${p.handle}`} onClick={() => setSel(p.handle)}
              className={`text-left rounded-2xl p-2 border-2 transition-all ${active ? "border-[#8BA888] bg-[#8BA888]/10" : "border-[#E8E6E1] hover:border-[#8BA888]/60"}`}>
              {p.image && <img src={p.image} alt={p.title} className="h-20 w-full object-cover rounded-xl mb-2" />}
              <p className="text-xs font-medium text-[#2D2825] line-clamp-2">{p.title}</p>
              {p.price && <p className="text-[11px] text-[#5f7d5c]">€ {p.price}</p>}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span data-testid="downsell-savings" className="inline-flex items-center gap-2 rounded-2xl bg-[#2D2825] text-white px-5 py-3 font-display text-2xl font-bold">
          Risparmi € {(((parseFloat(chosen?.price) || 0) * (single.discount_pct || 0)) / 100).toFixed(2)}
          <span className="text-sm font-semibold opacity-90">(−{single.discount_pct}%)</span>
        </span>
        <span className="text-xs text-[#7A7571]">Codice {single.discount_code} · valido 24h</span>
      </div>
      {chosen && (
        <a data-testid="downsell-cta" href={chosen.single_cart_url} target="_blank" rel="noreferrer"
          onClick={() => trackClick("single", reportId, nAnalisi, chosen.handle)}
          className="mt-5 inline-block rounded-full bg-[#2D2825] px-7 py-3.5 text-white font-medium hover:bg-black transition-colors">
          Provo “{chosen.title}” al {single.discount_pct}%
        </a>
      )}
    </div>
  );
};

const CartCard = ({ tier, kind, highlight, testId, onTrack }) => {
  const total = (tier.products || []).reduce((s, p) => s + (parseFloat(p.price) || 0), 0);
  const savings = (total * (tier.discount_pct || 0)) / 100;
  return (
  <div data-testid={testId} className={`relative rounded-3xl p-6 ${highlight ? "text-white ring-2 ring-[#8BA888]/50 md:-mt-3 md:mb-3" : "bg-white"}`}
    style={highlight ? { background: "linear-gradient(135deg,#8BA888,#5f7d5c)", boxShadow: "0 16px 44px rgba(139,168,136,0.4)" } : { boxShadow: "0 8px 32px rgba(45,40,37,0.08)" }}>
    {highlight && (
      <div data-testid={`${testId}-popular-badge`} className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 rounded-full bg-[#2D2825] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white shadow-lg whitespace-nowrap">
        <Star className="h-3.5 w-3.5 fill-[#E8C87A] text-[#E8C87A]" /> Il più scelto · 8 clienti su 10
      </div>
    )}
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className={`text-[10px] uppercase tracking-[0.25em] ${highlight ? "text-white/80" : "text-[#7A7571]"}`}>{kind}</p>
        <h3 className={`font-display text-xl ${highlight ? "text-white" : "text-[#2D2825]"}`}>{tier.title}</h3>
      </div>
      <ShoppingBag className={`h-5 w-5 ${highlight ? "text-white" : "text-[#5f7d5c]"}`} />
    </div>
    {tier.discount_code && (
      <div className={`mb-4 rounded-xl px-3 py-2 text-sm ${highlight ? "bg-white/15 text-white" : "bg-[#8BA888]/15 text-[#5f7d5c]"}`}>
        Sconto <strong>{tier.discount_pct}%</strong> · codice <span className="font-mono font-bold">{tier.discount_code}</span>
        <span className={`block text-[11px] ${highlight ? "text-white/90" : "text-[#7A7571]"}`}>⏱ valido 24 ore</span>
      </div>
    )}
    <div className="space-y-3">
      {tier.products.map((p, i) => (
        <div key={p.handle || i} className={`rounded-2xl p-4 ${highlight ? "bg-white/95" : "bg-[#FAF9F6]"}`}>
          <div className="flex gap-3">
            {p.image && <img src={p.image} alt={p.title} className="h-20 w-20 rounded-xl object-cover shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-[#2D2825] leading-snug">{p.title}</p>
                {p.price && <p className="text-sm font-bold text-[#5f7d5c] whitespace-nowrap">€ {p.price}</p>}
              </div>
              {p.reason && <p className="mt-1 text-xs leading-relaxed text-[#5f5a55]">{p.reason}</p>}
            </div>
          </div>
          {p.how_when && <p className="mt-2.5 text-[11px] text-[#7A7571] flex gap-1.5 items-start border-t border-black/5 pt-2"><Check className="h-3.5 w-3.5 text-[#8BA888] shrink-0 mt-0.5" />{p.how_when}</p>}
        </div>
      ))}
    </div>
    <div className="mt-5 space-y-2.5">
      <div className={`flex items-baseline justify-center gap-2 ${highlight ? "text-white" : "text-[#2D2825]"}`}>
        <span className={`text-sm line-through ${highlight ? "text-white/60" : "text-[#a09a94]"}`}>€ {total.toFixed(2)}</span>
        <span className="font-display text-3xl font-bold">€ {(total - savings).toFixed(2)}</span>
      </div>
      <div data-testid={`${testId}-savings`} className={`flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 font-semibold text-base ${highlight ? "bg-white/20 text-white" : "bg-[#8BA888]/15 text-[#5f7d5c]"}`}>
        Risparmi € {savings.toFixed(2)} (−{tier.discount_pct}%)
      </div>
      <div className={`flex items-center justify-center gap-4 text-xs ${highlight ? "text-white/90" : "text-[#7A7571]"}`}>
        <span>🚚 Spedizione gratuita</span><span>💳 Paga in 3 rate</span>
      </div>
    </div>
    <a href={tier.cart_url} target="_blank" rel="noreferrer" onClick={onTrack}
      className={`mt-4 flex items-center justify-center gap-2 rounded-full px-6 py-4 font-semibold text-base shadow-lg transition-transform hover:scale-[1.02] ${highlight ? "bg-white text-[#5f7d5c]" : "bg-[#5f7d5c] text-white hover:bg-[#4f6b4c]"}`}>
      <ShoppingBag className="h-5 w-5" /> Acquista la routine {kind} · € {(total - savings).toFixed(2)}
    </a>
    <p className={`mt-2 text-center text-[11px] ${highlight ? "text-white/80" : "text-[#a09a94]"}`}>Codice già applicato al carrello · valido 24h</p>
  </div>
  );
};
